import { Link, useLocation } from 'react-router-dom';
import { Instagram } from 'lucide-react';

/**
 * Shared footer for all public marketing/policy pages.
 * Uses React Router <Link> so each entry renders as a real <a href> in the DOM,
 * giving crawlers (Ahrefs, Google, Bing) proper internal outlinks on every page.
 *
 * Links are grouped into two tiers so the most useful ones stand out instead
 * of competing with legal/secondary links in one long undifferentiated row.
 */
const PRIMARY_LINKS = [
  { to: '/community', label: 'Community', testid: 'footer-community' },
  { to: '/contact', label: 'Support', testid: 'footer-contact' },
  { to: '/privacy', label: 'Privacy', testid: 'footer-privacy' },
  { to: '/terms', label: 'Terms', testid: 'footer-terms' },
];

const SECONDARY_LINKS = [
  { to: '/', label: 'Home', testid: 'footer-home' },
  { to: '/founding', label: 'Founding Filmmaker', testid: 'footer-founding' },
  { to: '/vs-spell-slate', label: 'Compare', testid: 'footer-compare' },
  { to: '/sms-consent', label: 'SMS Consent', testid: 'footer-sms-consent' },
];

function LinkRow({ links, className }) {
  if (links.length === 0) return null;
  return (
    <nav className={`flex items-center justify-center gap-3 flex-wrap px-4 ${className}`} aria-label="Site navigation">
      {links.map((l, i) => (
        <span key={l.to} className="flex items-center gap-3">
          <Link to={l.to} className="hover:text-[#D6B36A] font-body transition-colors" data-testid={l.testid}>
            {l.label}
          </Link>
          {i < links.length - 1 && <span className="text-[#333333]">|</span>}
        </span>
      ))}
    </nav>
  );
}

export default function PublicFooter({ testId = 'public-footer' }) {
  const { pathname } = useLocation();
  // Never link a page to itself (e.g. no "Home" link while already on the homepage).
  const primary = PRIMARY_LINKS.filter(l => l.to !== pathname);
  const secondary = SECONDARY_LINKS.filter(l => l.to !== pathname);

  return (
    <footer className="pt-10 pb-8 border-t border-white/[0.06] mt-16" data-testid={testId}>
      <LinkRow links={primary} className="text-sm text-[#A0A0A0]" />
      <LinkRow links={secondary} className="text-xs text-[#555555] mt-3" />
      <div className="flex items-center justify-center gap-4 mt-5">
        <a
          href="https://instagram.com/finalframesite"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Final Frame on Instagram (@finalframesite)"
          className="text-[#555555] hover:text-[#D6B36A] transition-colors"
          data-testid="footer-instagram"
        >
          <Instagram className="w-4 h-4" strokeWidth={1.5} />
        </a>
      </div>
      <p className="text-center text-[#555555] text-[10px] font-body italic mt-4">
        &copy; {new Date().getFullYear()} Within A Frame Productions &middot; Final Frame
      </p>
    </footer>
  );
}
